/**
 * Dispatch categories — the shared source of truth for every non-WO day
 * block on the dispatch board (PTO, Sick, Contract Job, Site Visit, …).
 *
 * Operators manage the list in Settings → Dispatch Categories; it is
 * persisted on the settings singleton as `dispatch_categories`. Anything
 * not in the list (legacy free-text reasons) still renders with the
 * neutral slate fallback.
 *
 * `kind`: "off" (PTO / Sick / Holiday — the tech is not working, cell is
 * red, automations pause) or "unavailable" (on the clock but not
 * schedulable — Training / Contract Job / Site Visit…, cell is orange).
 * `blocks_dispatch: false` means the day is *marked* but the tech can
 * still take a work order that day (Admin / Site Visit / Warranty).
 * `capture_details: true` asks for hours + notes when the block is made.
 */
import { useEffect, useState } from "react";
import { getSettings } from "@/lib/api";

export const CATEGORY_KINDS = {
  off:         { label: "Off",         color: "#EF4444", hint: "Not working — automations & notifications pause" },
  unavailable: { label: "Unavailable", color: "#F97316", hint: "On the clock, not schedulable for service work" },
};
const OFF_DEFAULT_IDS = new Set(["pto", "sick", "personal", "holiday"]);

export const DEFAULT_DISPATCH_CATEGORIES = [
  { id: "pto",          label: "PTO",             kind: "off",         color: "#F59E0B", blocks_dispatch: true,  capture_details: false },
  { id: "sick",         label: "Sick",            kind: "off",         color: "#EF4444", blocks_dispatch: true,  capture_details: false },
  { id: "personal",     label: "Personal",        kind: "off",         color: "#EC4899", blocks_dispatch: true,  capture_details: false },
  { id: "holiday",      label: "Holiday",         kind: "off",         color: "#64748B", blocks_dispatch: true,  capture_details: false },
  { id: "training",     label: "Training / Shop", kind: "unavailable", color: "#22C55E", blocks_dispatch: true,  capture_details: true },
  { id: "travel",       label: "Travel",          kind: "unavailable", color: "#14B8A6", blocks_dispatch: true,  capture_details: true },
  { id: "contract_job", label: "Contract Job",    kind: "unavailable", color: "#3B82F6", blocks_dispatch: false, capture_details: true, deprecated: true }, // replaced by contract-job dispatch cards — old blocks still render, hidden from the picker
  { id: "admin",        label: "Admin",           kind: "unavailable", color: "#8B5CF6", blocks_dispatch: false, capture_details: true },
  { id: "site_visit",   label: "Site Visit",      kind: "unavailable", color: "#06B6D4", blocks_dispatch: false, capture_details: true },
  { id: "warranty",     label: "Warranty",        kind: "unavailable", color: "#F97316", blocks_dispatch: false, capture_details: true },
  { id: "safety",       label: "Safety",          kind: "unavailable", color: "#EAB308", blocks_dispatch: false, capture_details: true },
];

// Unknown / legacy free-text reasons render as Off (red) — they block
// dispatching too, so the treatment matches.
export const FALLBACK_CATEGORY_COLOR = CATEGORY_KINDS.off.color;

export function normalizeKind(c) {
  if (c?.kind === "off" || c?.kind === "unavailable") return c.kind;
  return OFF_DEFAULT_IDS.has(String(c?.id || "").toLowerCase()) ? "off" : "unavailable";
}

let _cache = null;
let _inflight = null;

export function normalizeCategories(list) {
  if (!Array.isArray(list) || !list.length) return DEFAULT_DISPATCH_CATEGORIES;
  return list
    .filter(c => c && String(c.label || "").trim())
    .map(c => ({
      id: c.id || String(c.label).toLowerCase().replace(/[^a-z0-9]+/g, "_"),
      label: String(c.label).trim(),
      kind: normalizeKind(c),
      color: c.color || FALLBACK_CATEGORY_COLOR,
      blocks_dispatch: c.blocks_dispatch !== false,
      capture_details: !!c.capture_details,
      deprecated: !!c.deprecated || String(c.id || "").toLowerCase() === "contract_job" || String(c.label || "").trim().toLowerCase() === "contract job",
    }));
}

/** Categories offered in pickers — deprecated ones (Contract Job → now a dispatch card) still render on old blocks. */
export const selectableCategories = (list) => (list || []).filter(c => !c.deprecated);

export async function loadDispatchCategories() {
  if (_cache) return _cache;
  if (!_inflight) {
    _inflight = getSettings()
      .then(r => { _cache = normalizeCategories(r.data?.dispatch_categories); return _cache; })
      .catch(() => DEFAULT_DISPATCH_CATEGORIES)
      .finally(() => { _inflight = null; });
  }
  return _inflight;
}

export function refreshDispatchCategories(next) {
  _cache = next ? normalizeCategories(next) : null;
}

/** Categories for render. Starts with the defaults so the board never
 *  flashes uncoloured, then swaps in the operator's list. */
export function useDispatchCategories() {
  const [cats, setCats] = useState(_cache || DEFAULT_DISPATCH_CATEGORIES);
  useEffect(() => { let alive = true; loadDispatchCategories().then(c => { if (alive) setCats(c); }); return () => { alive = false; }; }, []);
  return cats;
}

/** Exact label match first, then a prefix match so legacy free-text
 *  reasons like "WARRANTY: Den Great Hall" still pick up their colour. */
export function findCategory(cats, reason) {
  const r = String(reason || "").trim().toLowerCase();
  if (!r) return null;
  const list = cats?.length ? cats : DEFAULT_DISPATCH_CATEGORIES;
  return (
    list.find(c => c.label.toLowerCase() === r) ||
    list.find(c => r.startsWith(c.label.toLowerCase())) ||
    null
  );
}

/** Cell colour is decided by the category's kind: red = Off, orange = Unavailable. */
export function categoryKind(cats, reason) {
  return findCategory(cats, reason)?.kind || "off";
}

export function categoryColor(cats, reason) {
  return CATEGORY_KINDS[categoryKind(cats, reason)].color;
}

/** Unknown/legacy reasons keep the old behaviour: they block dispatching. */
export function categoryBlocks(cats, reason) {
  const c = findCategory(cats, reason);
  return c ? c.blocks_dispatch !== false : true;
}
